/**
 * 退票人工複核細節 + 督導決議（M1 S5 / AC-E2-6）。
 *
 * 對應 BE：
 *   GET  /admin/refunds/manual-review/:id          細節 + refund_state_log timeline
 *   POST /admin/refunds/manual-review/:id/resolve  督導決議（complete / cancel）
 *
 * 決議寫 refund_state_log + audit_log；不可復原 → confirm 二次確認。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  getManualReview,
  resolveManualReview,
  type ManualReviewStateLogEntry,
} from '../lib/api-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

const ACTOR_LABEL: Record<ManualReviewStateLogEntry['actor_type'], string> = {
  system: '系統',
  admin: '管理員',
  partner: '通路夥伴',
};

export function ManualReviewDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const detailQuery = useQuery({
    queryKey: ['manual-review.detail', id],
    queryFn: () => getManualReview(id),
    enabled: id.length > 0,
  });

  const resolveMutation = useMutation({
    mutationFn: (decision: 'complete' | 'cancel') => resolveManualReview(id, decision, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manual-review.detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin.refunds.manual-review'] });
    },
  });

  const detail = detailQuery.data;
  const isManualReview = detail?.refund_state === 'manual_review';

  const handleResolve = (decision: 'complete' | 'cancel'): void => {
    const label = decision === 'complete' ? '核准完成退票' : '取消此退票';
    if (
      window.confirm(
        `確定要「${label}」？\n` +
          `退款 ID：${id}\n` +
          (note.trim() ? `備註：${note.trim()}\n` : '') +
          `\n決議會推進退款狀態機並寫入 refund_state_log + audit_log，不可復原。`,
      )
    ) {
      resolveMutation.mutate(decision);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">退票複核細節</h1>
        <div className="page-head__actions">
          <Link to="/refunds/manual-reviews">← 返回清單</Link>
        </div>
      </div>

      {detailQuery.isLoading && <div>載入中…</div>}
      {detailQuery.error && (
        <div className="error-banner">{(detailQuery.error as Error).message}</div>
      )}

      {detail && (
        <>
          <section
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: 16,
              margin: '12px 0',
            }}
          >
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '4px 12px',
                margin: 0,
              }}
            >
              <dt style={{ color: '#6b7280', fontSize: 13 }}>退款 ID</dt>
              <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>
                {detail.refund_id}
              </dd>
              <dt style={{ color: '#6b7280', fontSize: 13 }}>訂單 / 票券</dt>
              <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>
                {detail.order_id.slice(0, 8)}… /{' '}
                {detail.ticket_id ? `${detail.ticket_id.slice(0, 8)}…` : '—'}
              </dd>
              <dt style={{ color: '#6b7280', fontSize: 13 }}>退款狀態</dt>
              <dd style={{ margin: 0 }}>
                <span className={`badge ${isManualReview ? 'warning' : ''}`}>
                  {detail.refund_state}
                </span>
              </dd>
              <dt style={{ color: '#6b7280', fontSize: 13 }}>金額 / 方式</dt>
              <dd style={{ margin: 0 }}>
                NT$ {Number(detail.refund_amount).toLocaleString()}（{detail.refund_method}）
              </dd>
              <dt style={{ color: '#6b7280', fontSize: 13 }}>原因</dt>
              <dd style={{ margin: 0 }}>{detail.reason ?? '—'}</dd>
              <dt style={{ color: '#6b7280', fontSize: 13 }}>SLA 期限</dt>
              <dd style={{ margin: 0 }}>
                {formatDateTime(detail.manual_review_due_at)}
                {detail.overdue_minutes > 0 && (
                  <span className="badge danger" style={{ marginLeft: 8 }}>
                    逾時 {detail.overdue_minutes}m
                  </span>
                )}
              </dd>
            </dl>
          </section>

          {/* 督導決議 */}
          <section
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: 16,
              margin: '12px 0',
            }}
          >
            <h2 style={{ marginTop: 0, fontSize: 16 }}>督導決議（AC-E2-6）</h2>
            {!isManualReview ? (
              <div style={{ color: '#6b7280' }}>
                此退款目前狀態為 <strong>{detail.refund_state}</strong>，非
                manual_review，無法再決議。
              </div>
            ) : (
              <>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="決議備註（可選，1~500 字）"
                  style={{ width: '100%', maxWidth: 560, padding: 6, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => handleResolve('complete')}
                    disabled={resolveMutation.isPending}
                  >
                    核准完成退票
                  </button>
                  <button
                    onClick={() => handleResolve('cancel')}
                    disabled={resolveMutation.isPending}
                  >
                    取消此退票
                  </button>
                </div>
                {resolveMutation.error && (
                  <div className="error-banner" style={{ marginTop: 8 }}>
                    決議失敗：{(resolveMutation.error as Error).message}
                  </div>
                )}
                {resolveMutation.isSuccess && (
                  <div style={{ marginTop: 8, color: '#065f46' }}>
                    ✅ 決議已送出。
                    <button
                      style={{ marginLeft: 8 }}
                      onClick={() => navigate('/refunds/manual-reviews')}
                    >
                      返回清單
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* refund_state_log timeline */}
          <section style={{ margin: '12px 0' }}>
            <h2 style={{ fontSize: 16 }}>狀態歷程 refund_state_log</h2>
            <table>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>狀態轉移</th>
                  <th>操作者</th>
                  <th>原因</th>
                  <th>evidence hash</th>
                </tr>
              </thead>
              <tbody>
                {detail.state_log.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>
                      無歷程
                    </td>
                  </tr>
                )}
                {detail.state_log.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12 }}>{formatDateTime(e.occurred_at)}</td>
                    <td style={{ fontSize: 12 }}>
                      {e.from_state ?? '（初始）'} → <strong>{e.to_state}</strong>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {ACTOR_LABEL[e.actor_type]}
                      {e.actor_user_id ? ` (${e.actor_user_id.slice(0, 8)}…)` : ''}
                    </td>
                    <td style={{ fontSize: 12 }}>{e.reason}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>
                      {e.evidence_hash ? `${e.evidence_hash.slice(0, 12)}…` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
